<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import type { Pattern } from '@cross-my-heart/shared'
import { completedCells, type CompletedCell } from '../utils/progressThumbnail'

const props = defineProps<{
  pattern: Pattern | null
  /** Maximum width in CSS pixels. If not set, fills container */
  maxWidth?: number
  /** Maximum height in CSS pixels. If not set, fills container */
  maxHeight?: number
  /**
   * Completed-only mode (#195): when provided, paint the fabric colour and then
   * *only* these completed cells (each in its actual thread colour), instead of
   * the full chart — a progress thumbnail. An empty array renders bare fabric
   * (a 0%-complete project); omit the prop entirely for the full-pattern preview.
   */
  completions?: CompletedCell[]
  /**
   * Defer the first paint until the canvas scrolls into view (IntersectionObserver).
   * Used by long lists (home-screen project thumbnails) to avoid rendering hundreds
   * of offscreen canvases on mount. Off by default — single previews paint eagerly.
   */
  lazy?: boolean
}>()

const canvas = ref<HTMLCanvasElement | null>(null)
const container = ref<HTMLDivElement | null>(null)
// In lazy mode, gates the watch so a prop change before the element is visible
// doesn't bypass the IntersectionObserver and paint eagerly anyway.
let hasRendered = false

// Calculate display dimensions maintaining aspect ratio
const displaySize = computed(() => {
  if (!props.pattern) return { width: 0, height: 0 }

  const patternWidth = props.pattern.dimensions.width
  const patternHeight = props.pattern.dimensions.height

  // Use provided max dimensions or default to reasonable sizes
  const maxW = props.maxWidth ?? 400
  const maxH = props.maxHeight ?? 400

  // Calculate scale to fit within bounds while maintaining aspect ratio
  const scale = Math.min(maxW / patternWidth, maxH / patternHeight)

  return {
    width: Math.round(patternWidth * scale),
    height: Math.round(patternHeight * scale),
  }
})

// Render at higher resolution for retina displays
const pixelRatio = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1

function renderPattern() {
  if (!canvas.value || !props.pattern) return

  // Handle test environments where canvas may not fully support getContext
  if (typeof canvas.value.getContext !== 'function') return

  const ctx = canvas.value.getContext('2d')
  if (!ctx) return

  const { width, height } = props.pattern.dimensions
  const palette = props.pattern.palette

  // Set canvas internal size (1 pixel per stitch, scaled for retina)
  canvas.value.width = width * pixelRatio
  canvas.value.height = height * pixelRatio

  // Scale context for retina
  ctx.scale(pixelRatio, pixelRatio)

  // Fill with fabric background color
  ctx.fillStyle = props.pattern.fabric.color || '#FFFFFF'
  ctx.fillRect(0, 0, width, height)

  if (props.completions) {
    // Completed-only mode: paint just the worked cells over the fabric, so the
    // thumbnail reads like the real piece in progress (#195).
    for (const cell of completedCells(props.pattern, props.completions)) {
      ctx.fillStyle = cell.color
      ctx.fillRect(cell.x, cell.y, 1, 1)
    }
  } else {
    // Build a map for faster lookup of thread colors
    const threadColors = new Map<number, string>()
    for (const thread of palette) {
      threadColors.set(thread.index, thread.color)
    }

    // Draw each stitch as a single pixel
    for (const stitch of props.pattern.stitches) {
      const color = threadColors.get(stitch.threadIndex) ?? '#888888'
      ctx.fillStyle = color
      ctx.fillRect(stitch.x, stitch.y, 1, 1)
    }
  }

  hasRendered = true
}

// Re-render when the pattern or its completed cells change. In lazy mode, hold
// off until the first (observer-triggered) paint so we don't render offscreen.
watch(
  () => [props.pattern, props.completions],
  () => {
    if (props.lazy && !hasRendered) return
    renderPattern()
  }
)

let observer: IntersectionObserver | null = null

onMounted(() => {
  if (!props.lazy || typeof IntersectionObserver === 'undefined' || !container.value) {
    renderPattern()
    return
  }
  // Paint once the thumbnail nears the viewport; a margin pre-renders just before
  // it scrolls in so it's never visibly blank.
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        renderPattern()
        observer?.disconnect()
        observer = null
      }
    },
    { rootMargin: '200px' }
  )
  observer.observe(container.value)
})

onUnmounted(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <div ref="container" class="pattern-preview">
    <canvas
      v-if="pattern"
      ref="canvas"
      class="pattern-canvas"
      :style="{
        width: displaySize.width + 'px',
        height: displaySize.height + 'px',
      }"
    />
    <div v-else class="placeholder text-muted-foreground">
      <span class="placeholder-icon text-primary" aria-hidden="true">&#x2665;&#xFE0E;</span>
    </div>
  </div>
</template>

<style scoped>
.pattern-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.pattern-canvas {
  /* Crisp pixel rendering for the cross-stitch grid */
  image-rendering: pixelated;
  image-rendering: crisp-edges;
}

.placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.placeholder-icon {
  font-size: 3rem;
}
</style>
